import React, { useEffect, useRef, useState } from 'react';
import { Box, Heading, useToast, VStack } from '@chakra-ui/react';
import { Copy, Check } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { getCurrentDraws } from '../actions/drawActions';
import GenericTable from '../components/GenericTable';
import CountdownTimer from '../components/CountdownTimer';

const CurrentDraws = () => {
  const dispatch = useDispatch();
  const { slug } = useParams();
  const toast = useToast();
  const refreshingRef = useRef(false);

  const { currentDraws, loading, error } = useSelector((state) => state.draw);
  const [copiedId, setCopiedId] = useState(null);
  const [hiddenCurrentIds, setHiddenCurrentIds] = useState(() => new Set());
  const [version, setVersion] = useState(0);

  useEffect(() => {
    dispatch(getCurrentDraws());
  }, [dispatch]);

  useEffect(() => {
    setHiddenCurrentIds(new Set());
    setVersion((v) => v + 1);
  }, [currentDraws]);

  const handleCopyDrawId = async (drawId) => {
    try {
      await navigator.clipboard.writeText(drawId);
      setCopiedId(drawId);
      toast({
        title: 'Draw ID copied',
        status: 'success',
        duration: 1000,
        isClosable: true,
      });
      setTimeout(() => setCopiedId(null), 1500);
    } catch (err) {
      toast({
        title: 'Failed to copy Draw ID',
        status: 'error',
        duration: 2000,
        isClosable: true,
      });
    }
  };

  const handleDrawExpired = async (drawId) => {
    setHiddenCurrentIds((prev) => {
      const next = new Set(prev);
      next.add(drawId);
      return next;
    });
    setVersion((v) => v + 1);

    if (refreshingRef.current) return;
    refreshingRef.current = true;

    try {
      await dispatch(getCurrentDraws());
    } catch (fetchError) {
      toast({
        title: 'Error refreshing draws',
        description: fetchError.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setHiddenCurrentIds(new Set());
      setVersion((v) => v + 1);
      refreshingRef.current = false;
    }
  };

  const AutoRefreshTimer = ({ targetTime, onExpire }) => {
    const [active, setActive] = useState(true);
    return active ? (
      <CountdownTimer
        targetTime={targetTime}
        onExpire={() => {
          setActive(false);
          onExpire?.();
        }}
      />
    ) : null;
  };

  const columns = [
    {
      Header: 'Draw ID',
      accessor: 'draw_id',
      Cell: ({ value, row }) => {
        const gameNumber =
          row?.original?.gameNumber ??
          row?.original?.game_number ??
          row?.original?.gameCode ??
          row?.original?.game_code ??
          row?.original?.game_id ??
          '';
        const drawEpoch = Number(row?.original?.draw_time_epoc);
        const timeLeftSeconds =
          Number.isFinite(drawEpoch) ? Math.max(0, Math.floor((drawEpoch - Date.now()) / 1000)) : null;
        const query = new URLSearchParams({
          draw_id: value,
          game_id: gameNumber,
          next_draw: 'false',
          manual: 'false',
        });
        if (timeLeftSeconds !== null) {
          query.set('time_left', String(timeLeftSeconds));
        }
        const basePath = slug ? `/${slug}/draws` : '/draws';
        const href = `${basePath}?${query.toString()}`;

        return (
          <div className="flex items-center gap-2">
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline"
              onClick={(e) => e.stopPropagation()}
              title="Open draw details in a new tab"
            >
              {value}
            </a>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleCopyDrawId(value);
              }}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
              aria-label="Copy Draw ID"
              title="Copy Draw ID"
            >
              {copiedId === value ? (
                <Check className="w-4 h-4 text-green-500" />
              ) : (
                <Copy className="w-4 h-4 text-gray-500" />
              )}
            </button>
          </div>
        );
      },
    },
    { Header: 'Game', accessor: 'game_name' },
    { Header: 'Draw Time', accessor: 'draw_time', Cell: ({ value }) => new Date(value).toLocaleString() },
    { Header: 'Result', accessor: 'result', Cell: ({ value }) => value || '-' },
    { Header: 'Bonus', accessor: 'bonus', Cell: ({ value }) => value || '-' },
    {
      Header: 'Time Left',
      accessor: 'draw_time_epoc',
      Cell: ({ value, row }) => (
        <AutoRefreshTimer targetTime={value} onExpire={() => handleDrawExpired(row.original.draw_id)} />
      ),
    },
  ];

  const currentDrawsFiltered = (currentDraws || []).filter((d) => !hiddenCurrentIds.has(d.draw_id));

  if (loading) return <Box>Loading...</Box>;
  if (error) return <Box>Error: {error}</Box>;

  return (
    <Box p={5} key={version}>
      <VStack spacing={5} align="stretch">
        <Box>
          <Heading size="lg" mb={4}>
            Current Draws
          </Heading>
          <GenericTable
            isDrawTable={true}
            columns={columns}
            data={currentDrawsFiltered}
            initialPageSize={10}
          />
        </Box>
      </VStack>
    </Box>
  );
};

export default CurrentDraws;
